/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */

import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Option, Select } from 'carbon-react/lib/components/select';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, getCurrencySymbolFromCode, useIntl } from '@futrli/localisation';
import { UICarbonNumberField } from '@futrli/ui-components';

import { FreestyleMethodState, QuickActionsType } from '../../../types';
import { MAX_PERCENTAGE } from '../../../../../core/constants';
import * as utils from './utils';
import headerStyles from '../../prediction-header-bar/prediction-header-bar.scss';
import styles from './quick-actions.scss';
import { TooltipContainer } from './tooltip-container';

interface QuickActionsProps {
  currency?: string | null;
  freestyleContent?: FreestyleMethodState;
  onChange: (freestyleContent: FreestyleMethodState) => void;
}

export const QuickActions = ({ currency, freestyleContent, onChange }: QuickActionsProps) => {
  const [quickActionsType, setQuickActionsType] = useState(QuickActionsType.setSelectedTo);
  const [quickActionsValue, setQuickActionsValue] = useState('');

  const intl = useIntl();
  const currencySymbol = getCurrencySymbolFromCode(currency!);

  const handleQuickActionsTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setQuickActionsType(e.target.value as QuickActionsType),
    []
  );

  const handleQuickActionsValueChange = useCallback((value: string) => setQuickActionsValue(value), []);

  const handleFreestyleQuickActionsUpdate = useCallback(() => {
    if (!freestyleContent) {
      return;
    }

    switch (quickActionsType) {
      case QuickActionsType.setSelectedTo: {
        onChange(utils.setSelectedTo(freestyleContent, quickActionsValue));
        break;
      }
      case QuickActionsType.decreaseByAmount: {
        onChange(utils.decreaseByAmount(freestyleContent, quickActionsValue));
        break;
      }
      case QuickActionsType.decreaseByPercentage: {
        onChange(utils.decreaseByPercentage(freestyleContent, quickActionsValue));
        break;
      }
      case QuickActionsType.increaseByAmount: {
        onChange(utils.increaseByAmount(freestyleContent, quickActionsValue));
        break;
      }
      case QuickActionsType.increaseByPercentage: {
        onChange(utils.increaseByPercentage(freestyleContent, quickActionsValue));
        break;
      }
      case QuickActionsType.splitBetweenSelected: {
        onChange(utils.splitBetweenSelected(freestyleContent, quickActionsValue));
        break;
      }
      default: {
        // Unknown quick actions type, ignore...
      }
    }
  }, [quickActionsType, freestyleContent, quickActionsValue, onChange]);

  const { hasSelectedCells, hasSelectedCellsWithValues } = useMemo(() => {
    return {
      hasSelectedCells: freestyleContent && utils.hasSelectedCells(freestyleContent),
      hasSelectedCellsWithValues: freestyleContent && utils.hasSelectedCellsWithValues(freestyleContent)
    };
  }, [freestyleContent]);

  const isDisabledUpdate = useMemo(() => {
    if (quickActionsValue && freestyleContent) {
      // Disable update when non of the items are selected
      if (!hasSelectedCells) {
        return true;
      }

      // For set selected to and split between selected we don't need
      // to have initial values
      if (
        quickActionsType === QuickActionsType.setSelectedTo ||
        quickActionsType === QuickActionsType.splitBetweenSelected
      ) {
        return false;
      }

      // Disabled update when non of selected cells have value and we are increasing/decreasing
      // by amount/percentage
      return !hasSelectedCellsWithValues;
    }

    return true;
  }, [freestyleContent, hasSelectedCells, hasSelectedCellsWithValues, quickActionsType, quickActionsValue]);

  const showPercentageInput =
    quickActionsType === QuickActionsType.decreaseByPercentage ||
    quickActionsType === QuickActionsType.increaseByPercentage;

  const showAmountInput =
    quickActionsType === QuickActionsType.decreaseByAmount ||
    quickActionsType === QuickActionsType.increaseByAmount ||
    quickActionsType === QuickActionsType.setSelectedTo ||
    quickActionsType === QuickActionsType.splitBetweenSelected;

  const tooltipText = intl.formatMessage({
    id: 'predict.select_periods_to_enable',
    defaultMessage: 'Please select your periods to enable'
  });

  return (
    <>
      <TooltipContainer
        disableTooltip={typeof hasSelectedCells === 'undefined' ? true : hasSelectedCells}
        tooltipMessage={tooltipText}
      >
        <div className={styles.carbonContainer}>
          <div
            className={classNames(headerStyles.formInput, {
              [styles.disabledQuickActions]: !hasSelectedCells
            })}
          >
            <Select
              size="large"
              autoComplete="off"
              onChange={handleQuickActionsTypeChange}
              disabled={!hasSelectedCells}
              value={quickActionsType}
              label={intl.formatMessage({
                id: 'predict.quick_actions',
                defaultMessage: 'Quick actions'
              })}
              mb={0}
            >
              <Option
                key={QuickActionsType.setSelectedTo}
                value={QuickActionsType.setSelectedTo}
                text={intl.formatMessage({ id: 'predict.set_selected_to', defaultMessage: 'Set selected to' })}
              />
              <Option
                key={QuickActionsType.increaseByPercentage}
                value={QuickActionsType.increaseByPercentage}
                text={intl.formatMessage({
                  id: 'predict.increase_by_percentage',
                  defaultMessage: 'Increase by percentage'
                })}
              />
              <Option
                key={QuickActionsType.decreaseByPercentage}
                value={QuickActionsType.decreaseByPercentage}
                text={intl.formatMessage({
                  id: 'predict.decrease_by_percentage',
                  defaultMessage: 'Decrease by percentage'
                })}
              />
              <Option
                key={QuickActionsType.increaseByAmount}
                text={intl.formatMessage({ id: 'predict.increase_by_amount', defaultMessage: 'Increase by amount' })}
                value={QuickActionsType.increaseByAmount}
              />
              <Option
                key={QuickActionsType.decreaseByAmount}
                text={intl.formatMessage({ id: 'predict.decrease_by_amount', defaultMessage: 'Decrease by amount' })}
                value={QuickActionsType.decreaseByAmount}
              />
              <Option
                key={QuickActionsType.splitBetweenSelected}
                text={intl.formatMessage({
                  id: 'predict.split_between_selected',
                  defaultMessage: 'Split between selected'
                })}
                value={QuickActionsType.splitBetweenSelected}
              />
            </Select>
          </div>

          <UICarbonNumberField
            aria-label={intl.formatMessage({ id: 'common.update', defaultMessage: 'Update' })}
            autoComplete="off"
            className={classNames(headerStyles.formInput, headerStyles.unlabelledControl)}
            onChange={handleQuickActionsValueChange}
            value={quickActionsValue}
            type={showAmountInput ? 'currency' : 'percentage'}
            currencySymbol={showAmountInput ? currencySymbol : undefined}
            clampMax={showPercentageInput ? MAX_PERCENTAGE : undefined}
            clampMin={showPercentageInput ? 0 : undefined}
            data-testid="quick-actions-value"
            disabled={!hasSelectedCells}
            size="large"
            mb={0}
          />
          <Button
            disabled={isDisabledUpdate}
            mt="calc(21px + var(--global-space-comp-m))"
            onClick={handleFreestyleQuickActionsUpdate}
            size="large"
          >
            <FormattedMessage id="common.update" defaultMessage="Update" />
          </Button>
        </div>
      </TooltipContainer>
    </>
  );
};
